/**
 * Match clips kept on the phone until Wi‑Fi can take the upload.
 * IndexedDB holds the file bytes; the decision of when to wait is pure.
 */

const DB_NAME = "cm-match-clips";
const DB_VERSION = 1;
const STORE = "clips";

/**
 * Same IndexedDB clip store under another database name, so a feature's
 * backlog is never flushed by another feature's uploader.
 */
export function createLocalClipStore(dbName) {
  let storePromise = null;
  const open = () => {
    if (storePromise) return storePromise;
    storePromise = openNamedDb(dbName).catch((err) => {
      storePromise = null;
      throw err;
    });
    return storePromise;
  };
  return {
    async save(row) {
      const db = await open();
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(row);
      await finishTransaction(tx);
      return row;
    },
    async list() {
      const rows = await loadRowsFrom(await open());
      const ready = [];
      let copiesWork = true;
      for (const row of rows) {
        if (!copiesWork) {
          ready.push(row);
          continue;
        }
        const blob = row.blob ? await snapshotBlob(row.blob, row.mime) : null;
        const audioBlob = row.audioBlob
          ? await snapshotBlob(row.audioBlob, row.audioBlob.type)
          : null;
        if (row.blob && row.blob.size >= 256 && blob === row.blob) copiesWork = false;
        ready.push({ ...row, blob, audioBlob });
      }
      return ready.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    },
    async remove(id) {
      if (!id) return;
      const db = await open();
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(id);
      await finishTransaction(tx);
    },
    async count() {
      return (await loadRowsFrom(await open())).length;
    },
  };
}

function openNamedDb(name) {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("Local clip storage is not available"));
      return;
    }
    const request = indexedDB.open(name, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("by_created", "createdAt");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error || new Error("Could not open clip storage"));
  });
}

function loadRowsFrom(db) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const request = tx.objectStore(STORE).index("by_created").getAll();
    let raw = [];
    request.onsuccess = () => {
      raw = request.result || [];
    };
    request.onerror = () => reject(request.error || new Error("Clip storage failed"));
    tx.oncomplete = () => resolve(raw);
    tx.onerror = () => reject(tx.error || new Error("Clip storage failed"));
    tx.onabort = () => reject(tx.error || new Error("Clip storage was aborted"));
  });
}

/** True when a Wi‑Fi-only camera must keep this clip on the phone. */
export function matchUploadWaitsForWifi(wifiOnly, transport) {
  if (!wifiOnly) return false;
  const kind = String(transport || "");
  return kind === "cellular" || kind === "none";
}

/**
 * live  — upload each minute on any network.
 * wifi  — keep clips on the phone on mobile data, upload on Wi‑Fi.
 * phone — keep every clip on the phone while filming; upload on Wi‑Fi once
 *         filming stops, or when the user taps Upload.
 */
export const MATCH_UPLOAD_MODES = Object.freeze(["live", "wifi", "phone"]);

export function normalizeMatchUploadMode(raw, legacyWifiOnly = false) {
  const s = String(raw || "").trim().toLowerCase();
  if (MATCH_UPLOAD_MODES.includes(s)) return s;
  return legacyWifiOnly === true ? "wifi" : "live";
}

/**
 * Scorecam has no Wi‑Fi-only or Save-on-phone choice: its clips always upload
 * live. Match cameras keep the mode saved in camera settings.
 */
export function uploadModeForMatchClip(fields, fallback = "live") {
  const angle = String(fields?.angle_label || "").trim().toLowerCase();
  if (
    angle === "scorecam" ||
    angle === "score" ||
    angle === "score_camera" ||
    angle === "scorecam_video"
  ) {
    return "live";
  }
  return normalizeMatchUploadMode(fallback);
}

const ENCODE_PREFS_KEY = "cm-camera-encode-prefs";

/** Upload mode saved by the match camera settings. */
export function readMatchUploadMode() {
  try {
    const parsed = JSON.parse(localStorage.getItem(ENCODE_PREFS_KEY) || "null");
    return normalizeMatchUploadMode(parsed?.uploadMode, parsed?.wifiOnlyUpload);
  } catch {
    return "live";
  }
}

/** Change only the upload mode, keeping the other camera settings. */
export function writeMatchUploadMode(mode) {
  const next = normalizeMatchUploadMode(mode);
  try {
    const parsed = JSON.parse(localStorage.getItem(ENCODE_PREFS_KEY) || "null");
    const prefs = parsed && typeof parsed === "object" ? parsed : {};
    const { wifiOnlyUpload: _legacy, ...rest } = prefs;
    localStorage.setItem(ENCODE_PREFS_KEY, JSON.stringify({ ...rest, uploadMode: next }));
  } catch {
    /* private mode */
  }
  return next;
}

/**
 * Whether a clip goes to the server now or stays on the phone.
 * `force` is the user tapping Upload; only a phone with no network refuses it.
 * Phones that do not report the network ("unknown", e.g. iPhone Safari) upload
 * in Wi‑Fi mode and wait for a tap in phone mode.
 */
export function matchClipUploadPlan({
  mode = "live",
  transport = "unknown",
  recording = false,
  force = false,
} = {}) {
  const kind = String(transport || "unknown");
  if (kind === "none") return { upload: false, reason: "offline" };
  if (force) return { upload: true, reason: "" };
  const m = normalizeMatchUploadMode(mode);
  if (m === "live") return { upload: true, reason: "" };
  if (m === "wifi") {
    return kind === "cellular"
      ? { upload: false, reason: "cellular" }
      : { upload: true, reason: "" };
  }
  if (recording) return { upload: false, reason: "recording" };
  return kind === "wifi"
    ? { upload: true, reason: "" }
    : { upload: false, reason: kind === "cellular" ? "cellular" : "phone" };
}

export const LOCAL_CLIP_STORAGE_FULL = "LOCAL_CLIP_STORAGE_FULL";
/** Room kept free on the phone so the browser does not evict the store. */
const STORAGE_MARGIN_BYTES = 64 * 1024 * 1024;

function storageFullError(cause) {
  const err = new Error("This phone is out of storage for match videos.");
  err.code = LOCAL_CLIP_STORAGE_FULL;
  if (cause) err.cause = cause;
  return err;
}

export function isLocalClipStorageFull(err) {
  return (
    err?.code === LOCAL_CLIP_STORAGE_FULL ||
    err?.name === "QuotaExceededError" ||
    err?.cause?.name === "QuotaExceededError"
  );
}

/** Browser storage used / available for this site, or null when unknown. */
export async function estimateClipStorage() {
  try {
    const est = await navigator?.storage?.estimate?.();
    if (!est) return null;
    const usage = Number(est.usage) || 0;
    const quota = Number(est.quota) || 0;
    return { usage, quota, free: quota > 0 ? Math.max(0, quota - usage) : null };
  } catch {
    return null;
  }
}

let persistAsked = false;

/** Ask the browser not to evict saved clips under storage pressure. */
export async function requestPersistentClipStorage() {
  if (persistAsked) return null;
  persistAsked = true;
  try {
    if (await navigator?.storage?.persisted?.()) return true;
    return Boolean(await navigator?.storage?.persist?.());
  } catch {
    return null;
  }
}

const PENDING_HINT_KEY = "cm-local-clips-pending";

/** Cheap check so screens that never saved a clip do not open IndexedDB. */
export function localMatchClipsMayExist() {
  try {
    return localStorage.getItem(PENDING_HINT_KEY) === "1";
  } catch {
    return true;
  }
}

function setPendingHint(on) {
  try {
    if (on) localStorage.setItem(PENDING_HINT_KEY, "1");
    else localStorage.removeItem(PENDING_HINT_KEY);
  } catch {
    /* ignore */
  }
}

/** Small JPEG of the camera picture, shown in the phone gallery. */
export function captureClipThumb(video, width = 192) {
  try {
    const vw = Number(video?.videoWidth) || 0;
    const vh = Number(video?.videoHeight) || 0;
    if (!vw || !vh || video.readyState < 2) return null;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = Math.max(2, Math.round((width * vh) / vw));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.6);
  } catch {
    return null;
  }
}

/**
 * A failed upload stays on the phone when the network or the server failed.
 * A rejected request (wrong login, file refused) is not retried forever.
 */
export function matchClipShouldStayOnPhone(error) {
  const status = Number(error?.response?.status);
  if (!status) return true;
  if (status === 408 || status === 429) return true;
  return status >= 500;
}

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("Local clip storage is not available"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("by_created", "createdAt");
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        try {
          db.close();
        } catch {
          /* ignore */
        }
        dbPromise = null;
      };
      resolve(db);
    };
    request.onerror = () => {
      dbPromise = null;
      reject(request.error || new Error("Could not open clip storage"));
    };
  });
  return dbPromise;
}

function finishTransaction(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("Clip storage failed"));
    tx.onabort = () => reject(tx.error || new Error("Clip storage was aborted"));
  });
}

export async function saveLocalMatchClip({
  matchId,
  blob,
  fileName,
  mime,
  fields,
  audioBlob = null,
  thumb = null,
  info = null,
}) {
  const row = {
    id: `cml${Date.now()}${Math.floor(Math.random() * 1e6)}`,
    matchId: String(matchId || ""),
    blob,
    audioBlob: audioBlob && audioBlob.size >= 256 ? audioBlob : null,
    fileName: String(fileName || "clip.mp4"),
    mime: String(mime || blob?.type || "video/mp4"),
    fields: fields && typeof fields === "object" ? { ...fields } : {},
    byteSize: Number(blob?.size) || 0,
    thumb: typeof thumb === "string" && thumb.startsWith("data:image/") ? thumb : null,
    info: info && typeof info === "object" ? { ...info } : null,
    createdAt: Date.now(),
  };
  const bytes = row.byteSize + (Number(row.audioBlob?.size) || 0);
  const room = await estimateClipStorage();
  if (room?.free != null && room.free < bytes + STORAGE_MARGIN_BYTES) {
    throw storageFullError();
  }
  void requestPersistentClipStorage();
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(row);
    await finishTransaction(tx);
  } catch (err) {
    if (isLocalClipStorageFull(err)) throw storageFullError(err);
    throw err;
  }
  setPendingHint(true);
  return row;
}

function clipMeta(row) {
  return {
    id: row.id,
    matchId: row.matchId,
    fileName: row.fileName,
    mime: row.mime,
    fields: row.fields || {},
    byteSize: Number(row.byteSize) || Number(row.blob?.size) || 0,
    audioBytes: Number(row.audioBlob?.size) || 0,
    thumb: row.thumb || null,
    info: row.info || null,
    createdAt: row.createdAt || 0,
  };
}

/**
 * Every saved clip without its bytes, oldest first. A whole match can be
 * hundreds of MB, so the bytes are read one clip at a time on upload.
 */
export async function listLocalMatchClipMeta() {
  const rows = await loadStoredRows();
  setPendingHint(rows.length > 0);
  return rows
    .map(clipMeta)
    .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
}

/** One saved clip with readable bytes, or null when it is gone. */
export async function readLocalMatchClip(id) {
  if (!id) return null;
  const db = await openDb();
  const row = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const request = tx.objectStore(STORE).get(id);
    let found = null;
    request.onsuccess = () => {
      found = request.result || null;
    };
    request.onerror = () => reject(request.error || new Error("Clip storage failed"));
    tx.oncomplete = () => resolve(found);
    tx.onerror = () => reject(tx.error || new Error("Clip storage failed"));
    tx.onabort = () => reject(tx.error || new Error("Clip storage was aborted"));
  });
  if (!row) return null;
  const blob = row.blob ? await snapshotBlob(row.blob, row.mime) : null;
  const audioBlob = row.audioBlob
    ? await snapshotBlob(row.audioBlob, row.audioBlob.type)
    : null;
  return { ...row, blob, audioBlob };
}

function loadStoredRows() {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readonly");
        const request = tx.objectStore(STORE).index("by_created").getAll();
        let raw = [];
        request.onsuccess = () => {
          raw = request.result || [];
        };
        request.onerror = () => reject(request.error || new Error("Clip storage failed"));
        tx.oncomplete = () => resolve(raw);
        tx.onerror = () => reject(tx.error || new Error("Clip storage failed"));
        tx.onabort = () => reject(tx.error || new Error("Clip storage was aborted"));
      })
  );
}

/**
 * Copy clip bytes only after the IndexedDB transaction has finished.
 * Reading inside the success handler deadlocks the transaction, and closing
 * the database makes Safari hang for the whole upload.
 */
function snapshotBlob(blob, fallbackType) {
  if (!blob || typeof blob.size !== "number" || blob.size < 1) {
    return Promise.resolve(blob || null);
  }
  const type = String(blob.type || fallbackType || "video/mp4").split(";")[0];
  const read = new Promise((resolve, reject) => {
    setTimeout(() => {
      const finish = (buffer) => resolve(new Blob([buffer], { type }));
      if (typeof blob.arrayBuffer === "function") {
        blob.arrayBuffer().then(finish, reject);
        return;
      }
      if (typeof FileReader === "undefined") {
        resolve(blob);
        return;
      }
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error("Could not read clip"));
      reader.onload = () => finish(reader.result);
      reader.readAsArrayBuffer(blob);
    }, 0);
  });
  const giveUp = new Promise((resolve) => {
    setTimeout(() => resolve(blob), 8000);
  });
  return Promise.race([read, giveUp]).catch(() => blob);
}

export async function listLocalMatchClips() {
  const rows = await loadStoredRows();
  const ready = [];
  let copiesWork = true;
  for (const row of rows) {
    if (!copiesWork) {
      ready.push(row);
      continue;
    }
    const blob = await snapshotBlob(row.blob, row.mime);
    const audioBlob = row.audioBlob
      ? await snapshotBlob(row.audioBlob, row.audioBlob.type)
      : null;
    if (row.blob && row.blob.size >= 256 && blob === row.blob) copiesWork = false;
    ready.push({ ...row, blob, audioBlob });
  }
  return ready.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
}

export async function deleteLocalMatchClip(id) {
  if (!id) return;
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).delete(id);
  await finishTransaction(tx);
}

export async function summarizeLocalMatchClips() {
  const rows = await loadStoredRows();
  setPendingHint(rows.length > 0);
  return {
    count: rows.length,
    bytes: rows.reduce((sum, row) => sum + (Number(row.byteSize) || 0), 0),
  };
}

const ASSUMED_CLIP_MS = 60_000;

function clipOrder(a, b) {
  const ta = Number(a.fields?.t_ms);
  const tb = Number(b.fields?.t_ms);
  if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
  return (a.createdAt || 0) - (b.createdAt || 0);
}

/**
 * Clip metadata grouped into recordings: one camera filming one table, in
 * filming order. Newest recording first.
 */
export function groupLocalMatchClips(clips) {
  const groups = new Map();
  for (const clip of clips || []) {
    const fields = clip.fields || {};
    const table = fields.table_number ?? null;
    const angle = String(fields.angle_label || "");
    const key = `${clip.matchId}|${fields.capture_id || `t${table ?? ""}:${angle}`}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        matchId: String(clip.matchId || ""),
        captureId: fields.capture_id || null,
        table,
        angle,
        clips: [],
        bytes: 0,
        durationMs: 0,
        startedAt: clip.createdAt || 0,
        endedAt: clip.createdAt || 0,
        thumb: null,
        info: {},
      };
      groups.set(key, group);
    }
    group.clips.push(clip);
    group.bytes += (Number(clip.byteSize) || 0) + (Number(clip.audioBytes) || 0);
    group.durationMs += Number(clip.info?.durationMs) || ASSUMED_CLIP_MS;
    group.startedAt = Math.min(group.startedAt, clip.createdAt || group.startedAt);
    group.endedAt = Math.max(group.endedAt, clip.createdAt || 0);
    for (const [k, v] of Object.entries(clip.info || {})) {
      if (v !== "" && v != null) group.info[k] = v;
    }
  }
  const list = [...groups.values()];
  for (const group of list) {
    group.clips.sort(clipOrder);
    group.thumb = group.clips.find((c) => c.thumb)?.thumb || null;
  }
  return list.sort((a, b) => b.endedAt - a.endedAt);
}
